import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { usePagination, useLoading } from '../../hooks'
import { StatusChip, SubscriptionChip } from '../../components/common/Chips'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'
import RegisterOrgForm from '../../components/auth/RegisterOrgForm.jsx'
import { organizationApi } from '../../api/organizationApi'
import { exportToCSV } from '../../utils/exportUtils'

const Organizations = () => {
  const navigate = useNavigate()
  const [registerOpen, setRegisterOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const pagination = usePagination(10)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [filterStatus, setFilterStatus] = useState('all')
  const [organizations, setOrganizations] = useState([])
  const { loading, startLoading, stopLoading } = useLoading()

  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState('success')
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null)
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' })

  // Fetch organizations from API
  useEffect(() => {
    fetchOrganizations()
  }, [])

  const fetchOrganizations = async () => {
    try {
      startLoading()
      const response = await organizationApi.listOrganizations()
      setOrganizations(response.data || [])
    } catch (error) {
      console.error('Failed to fetch organizations:', error)
      setSnackbarMessage('Failed to load organizations')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      stopLoading()
    }
  }

  const handleMenuOpen = (event, org) => {
    setAnchorEl(event.currentTarget)
    setSelectedOrg(org)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleDeleteClick = (org) => {
    setSelectedOrg(org)
    setDeleteDialogOpen(true)
    handleMenuClose()
  }

  const handleConfirmDelete = async () => {
    if (!selectedOrg) return

    try {
      startLoading()
      await organizationApi.deleteOrganization(selectedOrg.id)
      // Optimistic update
      setOrganizations(prev => prev.filter(org => org.id !== selectedOrg.id))
      setSnackbarMessage('Organization deleted successfully')
      setSnackbarSeverity('success')
      setSnackbarOpen(true)
    } catch (error) {
      console.error('Failed to delete organization:', error)
      setSnackbarMessage('Failed to delete organization')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      stopLoading()
      setDeleteDialogOpen(false)
      setSelectedOrg(null)
    }
  }

  const handleViewDetails = () => {
    if (selectedOrg) {
      navigate(`/super-admin/organizations/${selectedOrg.id}`)
    }
    handleMenuClose()
  }

  const handleEdit = async (orgToEdit) => {
    // If called without arg (e.g. from menu where selectedOrg is already set), use selectedOrg
    const targetOrg = orgToEdit || selectedOrg

    if (targetOrg) {
      try {
        // Allow UI to update loading state
        setSelectedOrg(targetOrg)

        // Fetch fresh details before editing
        const response = await organizationApi.getOrganizationDetails(targetOrg.id)
        setSelectedOrg(response.data) // Update with fresh data
        setEditOpen(true)
      } catch (error) {
        console.error('Failed to fetch organization details:', error)
        setSnackbarMessage('Failed to load organization details')
        setSnackbarSeverity('error')
        setSnackbarOpen(true)
      } finally {
        handleMenuClose()
      }
    }
  }

  const handleRegisterSuccess = async (orgData) => {
    // Refresh the organizations list from the API
    await fetchOrganizations()
    setRegisterOpen(false)
    setSnackbarMessage('Organization registered successfully!')
    setSnackbarSeverity('success')
    setSnackbarOpen(true)
  }

  const handleEditSuccess = (updatedData) => {
    // Update organization in the list
    if (selectedOrg) {
      setOrganizations(prev =>
        prev.map(org =>
          org.id === selectedOrg.id
            ? { ...org, ...updatedData }
            : org
        )
      )
      setEditOpen(false)
      setSnackbarMessage(`Organization "${updatedData.name}" updated successfully!`)
      setSnackbarSeverity('success')
      setSnackbarOpen(true)
      setSelectedOrg(null)
    }
  }

  const filteredOrganizations = organizations.filter(org => {
    const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (org.contact_email && org.contact_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (org.contact_name && org.contact_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (org.contact_phone && org.contact_phone.includes(searchTerm))

    const matchesFilter = filterStatus === 'all' || org.status === filterStatus

    return matchesSearch && matchesFilter
  })

  // Sort organizations
  const sortedOrganizations = [...filteredOrganizations].sort((a, b) => {
    if (sortConfig.key) {
      if (sortConfig.key === 'created_at') {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;

        if (dateA !== dateB) {
          return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
        }

        // Fallback to sorting by ID if dates are identical or missing
        if (a.id !== b.id) {
          return sortConfig.direction === 'asc'
            ? (a.id > b.id ? 1 : -1)
            : (a.id < b.id ? 1 : -1);
        }
      } else {
        const aValue = a[sortConfig.key] !== undefined && a[sortConfig.key] !== null ? a[sortConfig.key] : '';
        const bValue = b[sortConfig.key] !== undefined && b[sortConfig.key] !== null ? b[sortConfig.key] : '';

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1
        }
      }
    }
    return 0
  })

  const handleSort = (key) => {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  const paginatedOrganizations = sortedOrganizations.slice(
    pagination.page * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
  )

  // Stats for the page
  const stats = {
    total: organizations.length,
    active: organizations.filter(o => o.status === 'active').length,
    inactive: organizations.filter(o => o.status === 'inactive').length,
  }

  const handleExport = () => {
    const columns = [
      { label: 'Name', key: 'name' },
      { label: 'Contact Name', key: 'contact_name' },
      { label: 'Email', key: 'contact_email' },
      { label: 'Phone', key: 'contact_phone' },
      { label: 'Status', key: 'status' },
      { label: 'Subscription', key: 'subscription_plan' },
      { label: 'Employees', key: 'employee_count' },
      { label: 'Devices', key: 'device_count' }
    ]
    exportToCSV(sortedOrganizations, columns, 'organizations-list')
  }

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* Register Form Dialog */}
      <RegisterOrgForm
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onSuccess={handleRegisterSuccess}
        showSnackbar={showSnackbar}
      />

      {/* Edit Form Dialog */}
      <RegisterOrgForm
        open={editOpen}
        onClose={() => {
          setEditOpen(false)
          setSelectedOrg(null)
        }}
        onSuccess={handleEditSuccess}
        editMode={true}
        initialData={selectedOrg}
        showSnackbar={showSnackbar}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Paper sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Organizations Management
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Manage all registered organizations
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>


        {/* Quick Stats */}
        {/* Quick Stats */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Organizations
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.active}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                  <CancelIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.inactive}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Inactive
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Filters and Search */}
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search organizations by name, email, contact person, or phone..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: { minWidth: { xs: '100%', sm: 300 } }
              }}
              sx={{ flexGrow: 1, width: { xs: '100%', md: 'auto' } }}
            />

            <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' } }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                size="small"
                onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                sx={{ flex: { xs: 1, md: 'none' } }}
              >
                Filter
              </Button>

              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                size="small"
                onClick={handleExport}
                sx={{ flex: { xs: 1, md: 'none' } }}
              >
                Export
              </Button>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setRegisterOpen(true)}
                size="small"
                sx={{ flex: { xs: 1, md: 'none' } }}
              >
                Register
              </Button>
            </Box>
          </Box>

          {/* Filter Status Display */}
          {filterStatus !== 'all' && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={`Status: ${filterStatus}`}
                size="small"
                onDelete={() => setFilterStatus('all')}
                color={
                  filterStatus === 'active' ? 'success' :
                    filterStatus === 'inactive' ? 'error' : 'default'
                }
              />
              <Typography variant="caption" color="textSecondary">
                Showing {filteredOrganizations.length} of {organizations.length} organizations
              </Typography>
            </Box>
          )}
        </Paper>


        {/* Organizations Table - Fixed Layout */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ minWidth: '200px', cursor: 'pointer' }}
                    onClick={() => handleSort('name')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Organization
                      {sortConfig.key === 'name' && (
                        <Typography variant="caption">
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: '150px' }}>Contact</TableCell>
                  <TableCell sx={{ width: '100px' }}>Employees</TableCell>
                  <TableCell sx={{ width: '80px' }}>Devices</TableCell>
                  <TableCell sx={{ width: '120px' }}>Status</TableCell>
                  <TableCell sx={{ width: '120px' }}>Subscription</TableCell>
                  <TableCell sx={{ width: '150px' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton columns={7} rows={5} avatar />
                ) : paginatedOrganizations.length > 0 ? (
                  paginatedOrganizations.map((org) => (
                    <TableRow key={org.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 40, height: 40, bgcolor: '#1976d2' }}>
                            <BusinessIcon sx={{ fontSize: 20 }} />
                          </Avatar>
                          <Box>
                            <Typography fontWeight="medium" variant="body2">
                              {org.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" noWrap>
                              {org.contact_email || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {org.contact_name || 'N/A'}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {org.contact_phone || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold" align="center">
                          {org.employee_count !== undefined ? org.employee_count.toLocaleString() : '0'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold" align="center">
                          {org.device_count !== undefined ? org.device_count : '0'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <StatusChip status={org.status} variant="outlined" />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <SubscriptionChip subscription={org.subscription_plan} />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/super-admin/organizations/${org.id}`)}
                            title="View Details"
                            sx={{
                              borderRadius: '4px',
                              padding: '6px',
                              '&:hover': { backgroundColor: '#e3f2fd' }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(org)}
                            title="Edit"
                            sx={{
                              borderRadius: '4px',
                              padding: '6px',
                              '&:hover': { backgroundColor: '#e8f5e8' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedOrg(org)
                              setDeleteDialogOpen(true)
                            }}
                            title="Delete"
                            sx={{
                              borderRadius: '4px',
                              padding: '6px',
                              '&:hover': { backgroundColor: '#ffebee' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">
                        {searchTerm ? 'No organizations found matching your search.' : 'No organizations found.'}
                      </Typography>
                      {searchTerm && (
                        <Button
                          size="small"
                          onClick={() => setSearchTerm('')}
                          sx={{ mt: 1 }}
                        >
                          Clear Search
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={sortedOrganizations.length}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Organization?</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <WarningIcon color="warning" fontSize="large" />
              <Typography>
                Are you sure you want to delete <strong>{selectedOrg?.name}</strong>? This action cannot be undone.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Filter Menu */}
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={() => setFilterMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setFilterStatus('all')
              setFilterMenuAnchor(null)
              pagination.setPage(0)
            }}
            selected={filterStatus === 'all'}
          >
            All Status
          </MenuItem>
          <MenuItem
            onClick={() => {
              setFilterStatus('active')
              setFilterMenuAnchor(null)
              pagination.setPage(0)
            }}
            selected={filterStatus === 'active'}
          >
            Active
          </MenuItem>
          <MenuItem
            onClick={() => {
              setFilterStatus('inactive')
              setFilterMenuAnchor(null)
              pagination.setPage(0)
            }}
            selected={filterStatus === 'inactive'}
          >
            Inactive
          </MenuItem>
        </Menu>

        {/* Action Menu (for mobile/alternative view) */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleViewDetails}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            View Details
          </MenuItem>
          <MenuItem onClick={() => handleEdit()}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Footer Info */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Total: {stats.total} organizations •
            Active: {stats.active}
          </Typography>
        </Box>
      </Paper>
    </Box>

  )
}

export default Organizations